import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SiteHeader from "@/components/layout/SiteHeader";
import HeaderSearch from "@/components/layout/HeaderSearch";
import Footer from "@/components/landing/Footer";
import CategorySidebar from "@/components/products/CategorySidebar";
import ProductCard from "@/components/products/ProductCard";
import { getCategoryBySlug } from "@/constants/categories";
import { useProductList } from "@/hooks/useProducts";

const PAGE_SIZE_OPTIONS = [12, 24, 36] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

function getPageNumbers(current: number, total: number): (number | "ellipsis")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

  const result: (number | "ellipsis")[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push("ellipsis");
    }
    result.push(sorted[i]);
  }
  return result;
}

export const ProductsPage = () => {
  const { slug: categorySlug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") ?? "";
  const category = categorySlug ? getCategoryBySlug(categorySlug) : undefined;
  const [pageSize, setPageSize] = useState<PageSize>(12);
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, isFetching } = useProductList({
    page,
    limit: pageSize,
    category: category?.slug,
    q: searchQuery || undefined,
    sort: "name",
  });

  const products = data?.items ?? [];
  const totalProducts = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  useEffect(() => {
    setPage(1);
  }, [searchQuery, category?.slug, pageSize]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [page, category?.slug, searchQuery]);

  const rangeStart = totalProducts === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalProducts);
  const pageNumbers = getPageNumbers(page, totalPages);

  const handlePageSizeChange = (size: PageSize) => {
    setPageSize(size);
    setPage(1);
  };

  const loading = isLoading;
  const error = isError ? "Failed to load products. Is the API running?" : null;

  if (categorySlug && !category) {
    return (
      <div className="bg-white text-vg-ink relative min-h-screen">
        <div className="noise-overlay" />
        <SiteHeader />
        <main className="pt-28 pb-24 px-4 sm:px-6 lg:px-12 max-w-[1600px] mx-auto">
          <h1 className="font-heading font-black text-4xl tracking-tighter">Category not found</h1>
          <p className="text-vg-gray mt-4">Try browsing all products instead.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-white text-vg-ink relative min-h-screen overflow-x-clip">
      <div className="noise-overlay" />
      <SiteHeader />

      <main className="pt-24 sm:pt-28 pb-16 sm:pb-24 overflow-x-clip" data-testid="products-page">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8 sm:mb-12 lg:mb-16"
          >
            <span className="font-mono-acc uppercase tracking-[0.2em] text-xs text-vg-gray">
              Shop / {category ? category.name : "All Products"}
            </span>
            <h1 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl lg:text-7xl mt-3 sm:mt-4 leading-[0.95]">
              {category ? (
                category.name
              ) : (
                <>
                  ALL <span className="text-outline">PRODUCTS</span>
                </>
              )}
            </h1>
            {category && !searchQuery && (
              <p className="max-w-xl text-vg-gray leading-relaxed mt-6 text-base md:text-lg">
                {category.description}
              </p>
            )}
            {searchQuery && (
              <p className="max-w-xl text-vg-gray leading-relaxed mt-6 text-base md:text-lg">
                Results for “{searchQuery}”
                {category ? ` in ${category.name}` : ""}
              </p>
            )}
          </motion.div>

          <div className="mb-8 md:hidden">
            <HeaderSearch className="w-full" />
          </div>

          <div className="grid lg:grid-cols-[280px_1fr] gap-5 sm:gap-6 lg:gap-16 min-w-0">
            <CategorySidebar activeSlug={category?.slug} />

            <section className="min-w-0 max-w-full">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mb-6 sm:mb-8 border-b border-black/10 pb-4">
                <p className="font-mono-acc uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[11px] sm:text-xs text-vg-gray break-words">
                  {loading
                    ? "Loading…"
                    : totalProducts === 0
                      ? searchQuery
                        ? "No matching products"
                        : "0 items"
                      : `Showing ${rangeStart}–${rangeEnd} of ${totalProducts} ${totalProducts === 1 ? "item" : "items"}`}
                  {isFetching && !loading ? " · Updating…" : ""}
                </p>

                {!loading && totalProducts > 0 && (
                  <div className="flex items-center gap-2 sm:gap-3 shrink-0" data-testid="products-page-size">
                    <span className="font-mono-acc uppercase tracking-[0.15em] text-[11px] sm:text-xs text-vg-gray">Show</span>
                    <div className="flex border border-black/10">
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <button
                          key={size}
                          type="button"
                          onClick={() => handlePageSizeChange(size)}
                          className={`px-2.5 sm:px-3 py-1.5 font-mono-acc text-[11px] sm:text-xs uppercase tracking-[0.15em] transition-colors ${
                            pageSize === size
                              ? "bg-vg-navy text-white"
                              : "bg-white text-vg-gray hover:text-vg-ink"
                          }`}
                          aria-pressed={pageSize === size}
                          data-testid={`products-page-size-${size}`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {error ? (
                <p className="text-red-600 py-16 text-center font-mono-acc text-sm" data-testid="products-error">
                  {error}
                </p>
              ) : loading ? (
                <p className="text-vg-gray py-16 text-center font-mono-acc text-sm uppercase tracking-[0.2em]">
                  Loading products…
                </p>
              ) : totalProducts === 0 ? (
                <p className="text-vg-gray py-16 text-center font-mono-acc text-sm uppercase tracking-[0.2em]">
                  {searchQuery ? "No products match your search." : "No products in this category yet."}
                </p>
              ) : (
                <>
                  <div
                    className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6"
                    data-testid="products-grid"
                  >
                    {products.map((product, index) => (
                      <ProductCard key={product.id} product={product} index={index} />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <nav
                      className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-black/10 pt-8"
                      aria-label="Products pagination"
                      data-testid="products-pagination"
                    >
                      <p className="font-mono-acc text-xs uppercase tracking-[0.2em] text-vg-gray">
                        Page {page} of {totalPages}
                      </p>

                      <div className="flex items-center gap-1 w-full sm:w-auto justify-center">
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="inline-flex items-center gap-1 px-3 py-2 font-mono-acc text-xs uppercase tracking-[0.15em] border border-black/10 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#f9fafb] transition-colors"
                          aria-label="Previous page"
                          data-testid="products-pagination-prev"
                        >
                          <ChevronLeft size={14} />
                          Prev
                        </button>

                        <div className="hidden sm:flex items-center gap-1">
                          {pageNumbers.map((item, i) =>
                            item === "ellipsis" ? (
                              <span
                                key={`ellipsis-${i}`}
                                className="px-2 font-mono-acc text-xs text-vg-gray"
                                aria-hidden
                              >
                                …
                              </span>
                            ) : (
                              <button
                                key={item}
                                type="button"
                                onClick={() => setPage(item)}
                                className={`min-w-9 px-3 py-2 font-mono-acc text-xs uppercase tracking-[0.15em] border transition-colors ${
                                  page === item
                                    ? "bg-vg-navy text-white border-vg-navy"
                                    : "border-black/10 text-vg-gray hover:text-vg-ink hover:bg-[#f9fafb]"
                                }`}
                                aria-current={page === item ? "page" : undefined}
                                data-testid={`products-pagination-page-${item}`}
                              >
                                {item}
                              </button>
                            ),
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className="inline-flex items-center gap-1 px-3 py-2 font-mono-acc text-xs uppercase tracking-[0.15em] border border-black/10 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#f9fafb] transition-colors"
                          aria-label="Next page"
                          data-testid="products-pagination-next"
                        >
                          Next
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </nav>
                  )}
                </>
              )}
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductsPage;
